import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmModal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Table } from '@/components/ui/Table'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { toastService } from '@/lib/toastService'
import { Box, Heading, HStack, IconButton, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { deletePackage, getPackages } from '../api/packagesApi'
import { PackageModal } from '../components/PackageModal'

const columnHelper = createColumnHelper()

export function PaymentPackages() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingPackage, setEditingPackage] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // Use URL filters hook
  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    is_published: '',
    page: 1,
    page_size: 20,
    ordering: 'name',
  })

  // Fetch packages with filters and pagination
  const { data, isLoading } = useQuery({
    queryKey: ['packages', filters.search, filters.is_published, filters.page, filters.page_size, filters.ordering],
    queryFn: () => {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
      }
      if (filters.search) params.search = filters.search
      if (filters.is_published !== '') params.is_published = filters.is_published
      if (filters.ordering) params.ordering = filters.ordering
      return getPackages(params)
    },
  })

  const totalCount = data?.meta?.pagination?.total_count || 0
  const pageCount = Math.ceil(totalCount / filters.page_size)

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deletePackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] })
      toastService.success({
        title: t('common.success'),
        description: t('packages.packageDeleted'),
      })
      setDeleteConfirm(null)
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('packages.packageDeleteError'),
      })
    },
  })

  const handleEdit = (pkg) => {
    setEditingPackage(pkg)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setEditingPackage(null)
    setIsModalOpen(true)
  }

  const handleDelete = (pkg) => {
    setDeleteConfirm(pkg)
  }

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  const columns = [
    columnHelper.accessor('name', {
      header: t('packages.name'),
      cell: (info) => (
        <Text fontWeight="600" color="#172B4D">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('price', {
      header: t('packages.price'),
      cell: (info) => (
        <Text>
          {info.getValue()} {t('packages.currency')}
        </Text>
      ),
    }),
    columnHelper.accessor('month_count', {
      header: t('packages.monthCount'),
      cell: (info) => (
        <Text>
          {info.getValue()} {t('packages.months')}
        </Text>
      ),
    }),
    columnHelper.accessor('is_published', {
      header: t('packages.published'),
      cell: (info) => (
        <Text
          px={2}
          py={1}
          borderRadius="md"
          bg={info.getValue() ? '#E3FCEF' : '#F4F5F7'}
          color={info.getValue() ? '#006644' : '#5E6C84'}
          fontSize="xs"
          fontWeight="600"
          display="inline-block"
        >
          {info.getValue() ? t('common.yes') : t('common.no')}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: (info) => (
        <HStack spacing={2}>
          <IconButton
            aria-label={t('common.edit')}
            size="sm"
            variant="ghost"
            onClick={() => handleEdit(info.row.original)}
          >
            <FiEdit2 />
          </IconButton>
          <IconButton
            aria-label={t('common.delete')}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => handleDelete(info.row.original)}
          >
            <FiTrash2 />
          </IconButton>
        </HStack>
      ),
    }),
  ]

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <Box p={8}>
      <HStack justify="space-between" mb={6}>
        <Heading size="2xl">{t('packages.title')}</Heading>
        <Button size="md" onClick={handleCreate} leftIcon={<FiPlus />}>
          {t('packages.createPackage')}
        </Button>
      </HStack>

      {/* Filters */}
      <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="#DFE1E6" mb={4}>
        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box flex="1">
            <Input
              placeholder={t('packages.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
            />
          </Box>
          <Box width={{ base: '100%', md: '200px' }}>
            <Select
              placeholder={t('packages.allPackages')}
              value={filters.is_published}
              onChange={(e) => updateFilters({ is_published: e.target.value })}
              options={[
                { value: 'true', label: t('packages.publishedOnly') },
                { value: 'false', label: t('packages.unpublishedOnly') },
              ]}
            />
          </Box>
        </Stack>
      </Box>

      <Box bg="white" borderRadius="md" border="1px solid" borderColor="#DFE1E6">
        <Table
          data={data?.data || []}
          columns={columns}
          enablePagination
          enableSorting
          manualPagination
          manualSorting
          pageCount={pageCount}
          totalCount={totalCount}
          state={{
            pagination: {
              pageIndex: filters.page - 1,
              pageSize: filters.page_size,
            },
            sorting: filters.ordering ? [{
              id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
              desc: filters.ordering.startsWith('-')
            }] : [],
          }}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </Box>

      {/* Create/Edit Modal */}
      <PackageModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingPackage(null)
        }}
        package={editingPackage}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={t('packages.deleteConfirmTitle')}
        message={t('packages.deleteConfirmMessage', { name: deleteConfirm?.name })}
        confirmText={t('common.delete')}
        isLoading={deleteMutation.isPending}
      />
    </Box>
  )
}

export default PaymentPackages
